import React, { useState, useEffect, useCallback, useRef } from 'react';
import { isEqual } from 'lodash';

import Dropdown from '../../../../components/Dropdown/Dropdown';
import { EnhancedErrorType } from './SliderErrors';

interface SliderErrorsFilterType {
  errors: EnhancedErrorType[];
  setSelectedErrors: React.Dispatch<React.SetStateAction<EnhancedErrorType[]>>;
}

const SliderErrorsFilter = ({ errors, setSelectedErrors }: SliderErrorsFilterType) => {
  const [checkedColumns, setCheckedColumns] = useState({}); // format is: {colHeader: boolean, colHeader2: boolean}

  const [columnsHeaders, setColumnsHeaders] = useState<Array<string>>([]);

  /*
  This function compares the checked columns with the errors object's
  columns to decide if it should show the error or not. If the column
  check is true and the error has the same column header, it shows the error.
  */
  const getAndSetFilteredColumns = useCallback(() => {
    const newSelectedErrors = errors.filter(column => {
      let isSelectedColumn = false;

      Object.keys(checkedColumns).forEach(check => {
        if (column.colHeader === check && checkedColumns[check]) {
          isSelectedColumn = true;
        }
      });

      return isSelectedColumn;
    });

    if (newSelectedErrors.length === 0) {
      setSelectedErrors(errors);
    } else {
      setSelectedErrors(newSelectedErrors);
    }
  }, [errors, checkedColumns, setSelectedErrors]);

  const checkedColumnsRef = useRef(checkedColumns);

  useEffect(() => {
    if (!isEqual(checkedColumnsRef.current, checkedColumns)) {
      checkedColumnsRef.current = checkedColumns;
      getAndSetFilteredColumns();
    }

    const uniqueColumnsHeaders = [...new Set(errors.map(error => error.colHeader))];

    setColumnsHeaders(uniqueColumnsHeaders as string[]);
  }, [checkedColumns, getAndSetFilteredColumns, errors]);

  const handleCheckboxChange = e => {
    setCheckedColumns({ ...checkedColumns, [e.currentTarget.name]: e.currentTarget.checked });
  };

  return (
    <Dropdown
      preventClose
      align="right"
      trigger={props => (
        <Dropdown.Trigger size="small" type="aura" {...props} data-testid="slider-errors-filtering-dropdown">
          Filter by column
        </Dropdown.Trigger>
      )}
    >
      {columnsHeaders?.map(column => {
        return (
          <Dropdown.Item
            key={`${column}-checkbox`}
            render={props => (
              <Dropdown.Checkbox
                {...props}
                id={`${column.toLowerCase().replace(' ', '')}-column`}
                onChange={handleCheckboxChange}
                label={column}
                name={column}
                checked={checkedColumns[column]}
              />
            )}
          />
        );
      })}
    </Dropdown>
  );
};

export default SliderErrorsFilter;
